
import React, { useEffect, useState } from "react";
import {
  CalendarDays,
  Clock3,
  Video,
  MapPin,
  Building2,
  BriefcaseBusiness,
  UserRound,
  Mail,
  Phone,
  RefreshCw,
  AlertCircle,
  ClipboardList,
  Hash,
} from "lucide-react";

import "./Interview.css";

const API_URL = "/api/interviews";

const formatDate = (value) => {
  if (!value) return "Not provided";

  const date = new Date(`${value}T00:00:00`);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatTime = (value) => {
  if (!value) return "Not provided";

  const match = String(value).match(/^(\d{1,2}):(\d{2})/);

  if (!match) return value;

  const date = new Date();
  date.setHours(Number(match[1]), Number(match[2]), 0, 0);

  return date.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "Not provided";
  }

  return String(value);
};

const Interview = () => {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadInterviews = async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");

      const response = await fetch(API_URL, {
        headers: token
          ? { Authorization: `Bearer ${token}` }
          : {},
      });

      if (!response.ok) {
        throw new Error(
          `Unable to load interviews (HTTP ${response.status}).`
        );
      }

      const data = await response.json();

      const records = Array.isArray(data)
        ? data
        : Array.isArray(data.data)
          ? data.data
          : Array.isArray(data.content)
            ? data.content
            : [];

      setInterviews(records);
    } catch (err) {
      setError(
        err.message ||
          "Something went wrong while loading interviews."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInterviews();
  }, []);

  return (
    <main className="admin-interviews-page">
      <header className="admin-interviews-header">
        <div className="admin-interviews-heading">
          <div className="admin-interviews-title-icon">
            <CalendarDays size={26} />
          </div>

          <div>
            <h1>Admin Interviews</h1>
            <p>
              View scheduled interviews and their associated
              trainer and job details.
            </p>
          </div>
        </div>

        <button
          type="button"
          className="admin-interviews-refresh"
          onClick={loadInterviews}
          disabled={loading}
          title="Refresh interviews"
        >
          <RefreshCw
            size={17}
            className={loading ? "admin-interviews-spinning" : ""}
          />
          Refresh
        </button>
      </header>

      <section className="admin-interviews-summary">
        <div className="admin-interviews-summary-icon">
          <ClipboardList size={22} />
        </div>

        <div>
          <span>Total interview records</span>
          <strong>
            {loading ? "—" : interviews.length}
          </strong>
        </div>
      </section>

      {loading && (
        <section className="admin-interviews-state">
          <RefreshCw
            size={30}
            className="admin-interviews-spinning"
          />
          <h2>Loading interviews...</h2>
          <p>Retrieving interview records from the server.</p>
        </section>
      )}

      {!loading && error && (
        <section className="admin-interviews-state admin-interviews-error">
          <AlertCircle size={32} />
          <h2>Couldn't load interviews</h2>
          <p>{error}</p>
          <button
            type="button"
            className="admin-interviews-retry"
            onClick={loadInterviews}
          >
            Try again
          </button>
        </section>
      )}

      {!loading && !error && interviews.length === 0 && (
        <section className="admin-interviews-state">
          <CalendarDays size={36} />
          <h2>No interviews found</h2>
          <p>
            Interview records will appear here when available.
          </p>
        </section>
      )}

      {!loading && !error && interviews.length > 0 && (
        <section className="admin-interviews-list">
          {interviews.map((item, index) => (
            <article
              className="admin-interview-card"
              key={item.interviewId ?? index}
            >
              <div className="admin-interview-card-header">
                <div className="admin-interview-job-icon">
                  <BriefcaseBusiness size={23} />
                </div>

                <div className="admin-interview-job-heading">
                  <span className="admin-interview-eyebrow">
                    INTERVIEW #{displayValue(item.interviewId)}
                  </span>

                  <h2>{displayValue(item.jobTitle)}</h2>

                  <p>
                    <Building2 size={15} />
                    {displayValue(item.companyName)}
                  </p>
                </div>

                <span
                  className={`admin-interview-status ${
                    String(item.interviewStatus).toUpperCase() ===
                    "SCHEDULED"
                      ? "is-scheduled"
                      : ""
                  }`}
                >
                  {displayValue(item.interviewStatus)}
                </span>
              </div>

              <div className="admin-interview-details-grid">
                <div className="admin-interview-detail">
                  <CalendarDays size={18} />
                  <div>
                    <span>Interview date</span>
                    <strong>{formatDate(item.interviewDate)}</strong>
                  </div>
                </div>

                <div className="admin-interview-detail">
                  <Clock3 size={18} />
                  <div>
                    <span>Interview time</span>
                    <strong>{formatTime(item.interviewTime)}</strong>
                  </div>
                </div>

                <div className="admin-interview-detail">
                  <Video size={18} />
                  <div>
                    <span>Interview mode</span>
                    <strong>{displayValue(item.interviewMode)}</strong>
                  </div>
                </div>

                <div className="admin-interview-detail">
                  <MapPin size={18} />
                  <div>
                    <span>Interview venue</span>
                    <strong>{displayValue(item.interviewLocation)}</strong>
                  </div>
                </div>
              </div>

              <div className="admin-interview-section">
                <h3>
                  <UserRound size={18} />
                  Trainer details
                </h3>

                <div className="admin-interview-info-grid">
                  <div>
                    <span>Trainer name</span>
                    <strong>{displayValue(item.trainerName)}</strong>
                  </div>

                  <div>
                    <span>Trainer email</span>
                    <strong>{displayValue(item.trainerEmail)}</strong>
                  </div>

                  <div>
                    <span>Trainer phone</span>
                    <strong>{displayValue(item.trainerPhone)}</strong>
                  </div>

                  <div>
                    <span>Trainer ID</span>
                    <strong>{displayValue(item.trainerId)}</strong>
                  </div>
                </div>
              </div>

              <div className="admin-interview-section">
                <h3>
                  <BriefcaseBusiness size={18} />
                  Job and partner details
                </h3>

                <div className="admin-interview-info-grid">
                  <div>
                    <span>Job ID</span>
                    <strong>{displayValue(item.jobId)}</strong>
                  </div>

                  <div>
                    <span>Job location</span>
                    <strong>{displayValue(item.jobLocation)}</strong>
                  </div>

                  <div>
                    <span>Partner ID</span>
                    <strong>{displayValue(item.partnerId)}</strong>
                  </div>

                  <div>
                    <span>Company</span>
                    <strong>{displayValue(item.companyName)}</strong>
                  </div>
                </div>
              </div>

              <div className="admin-interview-section">
                <h3>
                  <Mail size={18} />
                  Interviewer details
                </h3>

                <div className="admin-interview-info-grid">
                  <div>
                    <span>Interviewer name</span>
                    <strong>{displayValue(item.interviewerName)}</strong>
                  </div>

                  <div>
                    <span>Designation</span>
                    <strong>
                      {displayValue(item.interviewerDesignation)}
                    </strong>
                  </div>

                  <div>
                    <span>Email</span>
                    <strong>{displayValue(item.interviewerEmail)}</strong>
                  </div>

                  <div>
                    <span>Phone</span>
                    <strong>{displayValue(item.interviewerPhone)}</strong>
                  </div>
                </div>
              </div>

              {item.meetingLink && (
                <div className="admin-interview-section">
                  <h3>
                    <Video size={18} />
                    Meeting link
                  </h3>

                  <a
                    href={item.meetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="admin-interview-meeting-link"
                  >
                    Open meeting link
                  </a>
                </div>
              )}

              {item.remarks && (
                <div className="admin-interview-remarks">
                  <strong>Interview remarks</strong>
                  <p>{item.remarks}</p>
                </div>
              )}

              <footer className="admin-interview-footer">
                <span>
                  <Hash size={14} />
                  Application ID: {displayValue(item.applicationId)}
                </span>

                <span>
                  {item.isActive ? "Active record" : "Inactive record"}
                </span>
              </footer>
            </article>
          ))}
        </section>
      )}
    </main>
  );
};

export default Interview;
